import type { IItemView, IPersonView } from './model';
import { publicLabel } from './model';

/** The opening of the email when the firm hasn't written its own (Settings › Client upload links). */
export const DEFAULT_UPLOAD_INTRO: string =
  'Hello,\n\nTo move your application forward, we need the documents listed below. Please upload them with the secure link at the end of this email. They come straight to our office.';

export interface IUploadEmail {
  subject: string;
  html: string;
}

function escape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function paragraphs(text: string): string {
  return text.split(/\n{2,}/).map(p => p.trim()).filter(Boolean).map(p => `<p>${escape(p).replace(/\n/g, '<br>')}</p>`).join('\n');
}

/** "November 5, 2026" for an ISO time. */
function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * The email staff send the client with an upload link: one draft in their own Outlook, which they
 * read and send themselves. It has the file number, client, program, the documents still needed
 * (by person, first names only), the link and when it stops working. A restricted file's email has
 * the file number and the link only: no names, program or documents, in case someone else reads it.
 */
export function uploadEmail(d: {
  restricted: boolean;
  fileNumber?: string;
  clientName: string;
  program: string;
  intro?: string;
  people: IPersonView[];
  items: IItemView[];
  url: string;
  expires: string;
}): IUploadEmail {
  const file: string = d.fileNumber ? `File ${d.fileNumber}` : 'Your file';
  if (d.restricted) {
    return {
      subject: `${file}: upload link`,
      html: [
        `<p>${escape(file)}</p>`,
        `<p><a href="${escape(d.url)}">${escape(d.url)}</a></p>`
      ].join('\n')
    };
  }
  const name = (key: string): string => d.people.filter(p => p.key === key)[0]?.name ?? '';
  const groups: { title: string; items: IItemView[] }[] = [
    ...d.people.map(p => ({ title: p.name.replace(/^([^,]+), (.+)$/, '$2'), items: d.items.filter(i => i.personKey === p.key) })),
    { title: 'For the whole application', items: d.items.filter(i => !i.personKey || !name(i.personKey)) }
  ].filter(g => g.items.length);
  const list: string = groups.length === 1 && groups[0].title === 'For the whole application'
    ? `<ul>${groups[0].items.map(i => `<li>${escape(publicLabel(i.label))}</li>`).join('')}</ul>`
    : groups.map(g => `<p><strong>${escape(g.title)}</strong></p><ul>${g.items.map(i => `<li>${escape(publicLabel(i.label))}</li>`).join('')}</ul>`).join('\n');
  return {
    subject: `Documents for your ${d.program}${d.fileNumber ? ` (file ${d.fileNumber})` : ''}`,
    html: [
      paragraphs(d.intro?.trim() || DEFAULT_UPLOAD_INTRO),
      `<p>${d.fileNumber ? `File number: ${escape(d.fileNumber)}<br>` : ''}Client: ${escape(d.clientName)}<br>Program: ${escape(d.program)}</p>`,
      d.items.length ? `<p><strong>Documents we still need</strong></p>\n${list}` : '',
      `<p><a href="${escape(d.url)}">Upload your documents</a><br>This link is only for you and works until ${escape(longDate(d.expires))}. Documents not in English or French need a translation and the translator's affidavit too.</p>`
    ].filter(Boolean).join('\n')
  };
}
